/**
 * Configuration Helper Utilities
 *
 * Provides helper functions for:
 * - Loading and merging configurations
 * - Replacing placeholders in text
 * - Getting default configurations
 * - Building dynamic flows
 */

/**
 * Get default merchant configuration
 */
export function getDefaultConfiguration() {
  return {
    flow_config: {
      sections: [
        {
          id: 'photos',
          type: 'photo_upload',
          enabled: true,
          order: 1,
          config: {
            scope: 'booking',
            header_text: { de: 'Fotos hochladen', en: 'Upload Photos' },
            instructions: { de: 'Laden Sie Fotos hoch', en: 'Upload photos' },
            show_instructions: true,
            min_photos: 0,
            max_photos: 10
          }
        },
        {
          id: 'booking_questions',
          type: 'question_set',
          enabled: false,
          order: 2,
          config: {
            scope: 'booking',
            header_text: { de: 'Fragen', en: 'Questions' },
            show_instructions: false,
            questions: []
          }
        },
        {
          id: 'item_questions',
          type: 'question_set',
          enabled: false,
          order: 3,
          config: {
            scope: 'item',
            mode: 'per_inventory_item',
            pagination: 'one_per_page',
            skip_if_no_items: true,
            show_item_count: true,
            questions: [],
            item_photos_config: {
              enabled: false,
              mode: 'optional',
              min_photos: 0,
              max_photos: 5
            }
          }
        },
        {
          id: 'thank_you',
          type: 'completion',
          enabled: true,
          order: 4,
          config: {
            header_text: { de: 'Vielen Dank!', en: 'Thank You!' },
            message: { de: 'Deine Rückgabe wurde erfasst.', en: 'Your return has been recorded.' },
            review_request: { enabled: false },
            instruction_boxes: []
          }
        }
      ]
    },
    auto_complete_booking: true,
    allow_return_updates: true
  };
}

/**
 * Replace placeholders in text with actual values
 *
 * Available placeholders:
 * - {item_name} - from booking_items.item_name
 * - {item_number} - from inventory_items.item_number
 * - {size} - from booking_items.size or inventory_items.size
 * - {code} - from booking_items.code
 * - {combination_code} - from inventory_items.combination_code
 * - {product_type} - from booking_items.product_type
 * - {quantity} - from booking_items.quantity
 * - {item_display_name} - computed name (item_name + item_number)
 */
export function replacePlaceholders(text, bookingItem, inventoryItem) {
  if (!text) return '';

  let result = text;

  // Booking item fields
  if (bookingItem) {
    result = result.replace(/{item_name}/g, bookingItem.item_name || '');
    result = result.replace(/{size}/g, bookingItem.size || '');
    result = result.replace(/{code}/g, bookingItem.code || '');
    result = result.replace(/{product_type}/g, bookingItem.product_type || '');
    result = result.replace(/{quantity}/g, bookingItem.quantity || '1');
  }

  // Inventory item fields (if available)
  if (inventoryItem) {
    result = result.replace(/{item_number}/g, inventoryItem.item_number || '');
    result = result.replace(/{combination_code}/g, inventoryItem.combination_code || '');
    result = result.replace(/{nr}/g, inventoryItem.nr || '');

    // Size can come from either table, prefer inventory_items
    if (inventoryItem.size) {
      result = result.replace(/{size}/g, inventoryItem.size);
    }
  } else {
    // If no inventory item, remove unfilled placeholders
    result = result.replace(/{item_number}/g, '');
    result = result.replace(/{combination_code}/g, '');
    result = result.replace(/{nr}/g, '');
  }

  // Computed display name
  const displayName = computeItemDisplayName(bookingItem, inventoryItem);
  result = result.replace(/{item_display_name}/g, displayName);

  // Clean up extra spaces
  return result.replace(/\s+/g, ' ').trim();
}

/**
 * Compute a display name for an item
 */
export function computeItemDisplayName(bookingItem, inventoryItem) {
  if (!bookingItem) return '';

  const itemName = bookingItem.item_name || '';
  const itemNumber = inventoryItem?.item_number || '';

  if (itemNumber) {
    return `${itemName} ${itemNumber}`.trim();
  }

  return itemName;
}

/**
 * Build dynamic flow based on configuration and booking data
 * Filters out disabled sections and sections with no content
 */
export function buildReturnFlow(config, booking, bookingItems) {
  if (!config?.flow_config?.sections) {
    console.warn('No flow_config.sections found, using defaults');
    const defaultConfig = getDefaultConfiguration();
    return defaultConfig.flow_config.sections.filter(s => s.enabled);
  }

  const sections = [];

  for (const section of config.flow_config.sections) {
    // Skip disabled sections
    if (!section.enabled) continue;

    // Note: We no longer skip question sets with empty questions
    // This allows sections to be shown even if they have no questions yet
    // The DynamicQuestionSet component handles auto-completion

    // Skip item questions if no items and skip_if_no_items is true
    if (section.config?.scope === 'item' &&
        section.config?.skip_if_no_items &&
        (!bookingItems || bookingItems.length === 0)) {
      continue;
    }

    sections.push(section);
  }

  // Sort by order
  sections.sort((a, b) => a.order - b.order);

  return sections;
}

/**
 * Determine if user can proceed based on current answers
 */
export function canProceedFromQuestion(question, answers) {
  const answer = answers[question.id];

  // Must have an answer if question is required
  if (question.required && !answer) {
    return false;
  }

  // If no answer selected yet, allow (unless required)
  if (!answer && !question.required) {
    return true;
  }

  // Find the selected option
  const selectedOption = question.options?.find(opt => opt.value === answer);
  if (!selectedOption) {
    return !question.required; // Allow if not required
  }

  // Check if blocked
  if (selectedOption.action === 'block') {
    return false;
  }

  // Check if requires details and details are provided
  if (selectedOption.action === 'require_details') {
    const detailsFieldName = selectedOption.details_config?.field_name;
    if (!detailsFieldName) return false;

    const detailsValue = answers[detailsFieldName];
    if (selectedOption.details_config?.required && !detailsValue?.trim()) {
      return false;
    }
  }

  return true;
}

/**
 * Determine if user can proceed from all questions in a section
 */
export function canProceedFromSection(section, answers) {
  if (section.type !== 'question_set') {
    return true; // Non-question sections are always passable
  }

  const questions = section.config?.questions || [];

  for (const question of questions) {
    if (!canProceedFromQuestion(question, answers)) {
      return false;
    }
  }

  return true;
}

/**
 * Generate human-readable description from answers
 */
export function generateDescription(answers, questions) {
  const parts = [];

  for (const question of questions) {
    const answer = answers[question.id];
    if (!answer) continue;

    const option = question.options?.find(opt => opt.value === answer);
    if (!option) continue;

    // Add question and answer
    const questionText = question.text?.en || question.text?.de || question.id;
    const answerText = option.label?.en || option.label?.de || answer;

    let line = `${questionText}: ${answerText}`;

    // Add details if present
    if (option.action === 'require_details') {
      const detailsFieldName = option.details_config?.field_name;
      const details = answers[detailsFieldName];
      if (details) {
        line += ` - ${details}`;
      }
    }

    parts.push(line);
  }

  return parts.join('\n');
}

/**
 * Determine overall status from answers
 */
export function determineOverallStatus(bookingAnswers, itemReturns) {
  // Check booking-level answers for issues
  const hasBookingIssues = Object.values(bookingAnswers).some(value =>
    value && (
      value.includes('problem') ||
      value.includes('issue') ||
      value.includes('damage') ||
      value.includes('missing')
    )
  );

  if (hasBookingIssues) return 'issues_reported';

  // Check item-level returns for issues
  if (itemReturns && itemReturns.length > 0) {
    const hasItemIssues = itemReturns.some(item =>
      item.item_status && item.item_status !== 'ok'
    );

    if (hasItemIssues) return 'issues_reported';
  }

  return 'all_ok';
}

/**
 * Determine item status from item answers
 */
export function determineItemStatus(itemAnswers) {
  // Look for common issue indicators in answers
  const answersString = JSON.stringify(itemAnswers).toLowerCase();

  if (answersString.includes('missing')) return 'missing';
  if (answersString.includes('damaged') || answersString.includes('damage')) return 'damaged';
  if (answersString.includes('problem') || answersString.includes('issue')) return 'issue';

  return 'ok';
}

/**
 * Check if item-level photos are required based on answers
 */
export function areItemPhotosRequired(itemAnswers, photoConfig) {
  if (!photoConfig || !photoConfig.enabled) {
    return false;
  }

  if (photoConfig.mode === 'required') {
    return true;
  }

  if (photoConfig.mode === 'required_if_issues') {
    // Check if any answer indicates issues
    const hasIssues = Object.entries(itemAnswers).some(([key, value]) => {
      if (key.endsWith('_details')) return false; // Skip detail fields
      return value && (
        value === 'problems' ||
        value === 'issues' ||
        value === 'damage_found' ||
        value === 'damaged' ||
        value === 'missing'
      );
    });

    return hasIssues;
  }

  return false; // optional mode
}

/**
 * Get completed sections list from answers
 */
export function getCompletedSections(flow, bookingAnswers, itemReturns, hasPhotos) {
  const completed = [];

  for (const section of flow) {
    if (section.type === 'photo_upload' && hasPhotos) {
      completed.push(section.id);
    } else if (section.type === 'question_set' && section.config?.scope === 'booking') {
      if (Object.keys(bookingAnswers).length > 0) {
        completed.push(section.id);
      }
    } else if (section.type === 'question_set' && section.config?.scope === 'item') {
      if (itemReturns && itemReturns.length > 0) {
        completed.push(section.id);
      }
    } else if (section.type === 'completion') {
      completed.push(section.id);
    }
  }

  return completed;
}

export default {
  getDefaultConfiguration,
  replacePlaceholders,
  computeItemDisplayName,
  buildReturnFlow,
  canProceedFromQuestion,
  canProceedFromSection,
  generateDescription,
  determineOverallStatus,
  determineItemStatus,
  areItemPhotosRequired,
  getCompletedSections
};
